import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { Loader2, Lock, ShieldCheck, AlertTriangle, CheckCircle2 } from "lucide-react";
import KittLogo from "../components/KittLogo";

export default function RegisterViaInvite() {
    const [params] = useSearchParams();
    const token = params.get("invite") || params.get("token") || "";

    const [verifyState, setVerifyState] = useState({ status: "checking", info: null, reason: "" });
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [done, setDone] = useState(false);

    useEffect(() => {
        let cancelled = false;
        if (!token) {
            setVerifyState({ status: "invalid", info: null, reason: "No invitation token in the URL." });
            return;
        }
        (async () => {
            try {
                const { data } = await api.get(`/auth/invitations/${encodeURIComponent(token)}/verify`);
                if (cancelled) return;
                if (data?.valid) {
                    setVerifyState({ status: "valid", info: data, reason: "" });
                } else {
                    setVerifyState({ status: "invalid", info: null, reason: data?.reason || "Invalid token" });
                }
            } catch (err) {
                if (cancelled) return;
                setVerifyState({
                    status: "invalid",
                    info: null,
                    reason: formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Could not verify invitation.",
                });
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const passwordMismatch = useMemo(
        () => confirm.length > 0 && confirm !== password,
        [password, confirm]
    );
    const canSubmit =
        verifyState.status === "valid" &&
        name.trim().length >= 2 &&
        password.length >= 8 &&
        password === confirm &&
        !submitting;

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitError("");
        setSubmitting(true);
        try {
            await api.post("/auth/register-via-invite", {
                token,
                name: name.trim(),
                password,
            });
            setDone(true);
        } catch (err) {
            setSubmitError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-jet text-snow flex" data-testid="register-invite-page">
            {/* Left — branding panel (mirrors Login) */}
            <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-hairline">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,106,0,0.08),transparent_50%)]" />
                <div className="absolute inset-x-0 h-px bg-signal/40 animate-scan" />
                <div className="relative z-10 p-12 flex flex-col justify-between w-full">
                    <div className="flex items-center">
                        <KittLogo size={120} withGlow testId="register-kitt-logo" />
                    </div>

                    <div className="space-y-6">
                        <div className="overline">Restricted Onboarding</div>
                        <h1 className="heading-display text-4xl xl:text-5xl leading-[1.05]">
                            Invite-only access.<br />
                            <span className="text-signal">Single-use token.</span>
                        </h1>
                        <p className="text-snow-dim text-sm max-w-md leading-relaxed">
                            KITT does not allow open self-registration. Your invitation
                            token is single-use and expires in 7 days. After registration,
                            an administrator must approve your account before access is granted.
                        </p>
                    </div>

                    <div className="grid grid-cols-3 gap-4 text-xs">
                        <div className="border-l-2 border-signal pl-3">
                            <div className="overline">Step</div>
                            <div className="heading-display text-2xl mt-1">01</div>
                            <div className="text-[10px] text-snow-muted mt-1 font-mono">Verify token</div>
                        </div>
                        <div className="border-l-2 border-signal pl-3">
                            <div className="overline">Step</div>
                            <div className="heading-display text-2xl mt-1">02</div>
                            <div className="text-[10px] text-snow-muted mt-1 font-mono">Set password</div>
                        </div>
                        <div className="border-l-2 border-hairline pl-3">
                            <div className="overline">Step</div>
                            <div className="heading-display text-2xl mt-1">03</div>
                            <div className="text-[10px] text-snow-muted mt-1 font-mono">Admin approval</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Right — form panel */}
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="overline mb-2 flex items-center gap-2">
                        <ShieldCheck className="w-3 h-3 text-signal" /> Invitation
                    </div>
                    <h2 className="heading-display text-3xl mb-1">Activate Account</h2>
                    <p className="text-snow-dim text-sm mb-8">
                        Complete onboarding using the invitation issued to you.
                    </p>

                    {verifyState.status === "checking" && (
                        <div className="flex items-center gap-3 text-snow-dim font-mono text-sm" data-testid="invite-verifying">
                            <Loader2 className="w-4 h-4 animate-spin text-signal" />
                            Verifying invitation token…
                        </div>
                    )}

                    {verifyState.status === "invalid" && (
                        <div className="space-y-4" data-testid="invite-invalid">
                            <div className="border border-red-500/50 bg-red-500/5 px-4 py-3 flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm font-mono">
                                    <div className="text-red-400 font-bold uppercase tracking-wider text-xs mb-1">Invitation Unavailable</div>
                                    <div className="text-snow-dim" data-testid="invite-invalid-reason">{verifyState.reason}</div>
                                </div>
                            </div>
                            <p className="text-[12px] text-snow-muted font-mono leading-relaxed">
                                Invitation tokens are single-use and expire after 7 days. If your link no longer works,
                                contact your administrator to issue a fresh invitation.
                            </p>
                            <Link
                                to="/login"
                                className="cerb-btn-secondary inline-flex"
                                data-testid="invite-back-to-login"
                            >
                                ← Return to Sign-In
                            </Link>
                        </div>
                    )}

                    {verifyState.status === "valid" && !done && (
                        <form onSubmit={onSubmit} className="space-y-5" data-testid="invite-form">
                            <div className="border border-signal bg-signal/5 px-3 py-2.5 text-[11px] font-mono text-signal flex items-start gap-2" data-testid="invite-valid-banner">
                                <ShieldCheck className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                <div>
                                    <div className="font-bold uppercase tracking-wider">Token Verified</div>
                                    <div className="text-snow-dim mt-0.5">
                                        Email: <span className="text-snow">{verifyState.info?.email}</span>
                                        {verifyState.info?.role_to_assign && (
                                            <> · Role on approval: <span className="text-snow uppercase tracking-wider">{verifyState.info.role_to_assign}</span></>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="overline block mb-2">Full Name</label>
                                <input
                                    data-testid="invite-name-input"
                                    type="text"
                                    required
                                    minLength={2}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full bg-jet-elev1 border border-hairline px-3 py-3 text-snow font-mono text-sm focus:border-signal outline-none transition-colors"
                                    placeholder="Avery Kim"
                                />
                            </div>

                            <div>
                                <label className="overline block mb-2">Password (min 8 chars)</label>
                                <input
                                    data-testid="invite-password-input"
                                    type="password"
                                    required
                                    minLength={8}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-jet-elev1 border border-hairline px-3 py-3 text-snow font-mono text-sm focus:border-signal outline-none transition-colors"
                                    placeholder="••••••••"
                                />
                            </div>

                            <div>
                                <label className="overline block mb-2">Confirm Password</label>
                                <input
                                    data-testid="invite-confirm-input"
                                    type="password"
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    className={`w-full bg-jet-elev1 border px-3 py-3 text-snow font-mono text-sm outline-none transition-colors ${
                                        passwordMismatch ? "border-red-500/60" : "border-hairline focus:border-signal"
                                    }`}
                                    placeholder="••••••••"
                                />
                                {passwordMismatch && (
                                    <div className="text-[11px] text-red-400 font-mono mt-1.5" data-testid="invite-password-mismatch">
                                        Passwords do not match.
                                    </div>
                                )}
                            </div>

                            {submitError && (
                                <div
                                    data-testid="invite-submit-error"
                                    className="border border-red-500/50 bg-red-500/5 text-red-400 px-3 py-2 text-xs font-mono"
                                >
                                    {submitError}
                                </div>
                            )}

                            <button
                                data-testid="invite-submit-btn"
                                type="submit"
                                disabled={!canSubmit}
                                className="w-full bg-signal text-jet hover:bg-signal-hover px-6 py-3 transition-colors font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                                Create Account
                            </button>

                            <div className="border-t border-hairline pt-4 text-[11px] text-snow-muted font-mono">
                                After registration, your account enters the
                                <span className="text-signal"> Pending </span>
                                queue. An administrator will review and approve before you can sign in.
                            </div>
                        </form>
                    )}

                    {done && (
                        <div className="space-y-4" data-testid="invite-success">
                            <div className="border border-signal bg-signal/5 px-4 py-4 flex items-start gap-3">
                                <CheckCircle2 className="w-6 h-6 text-signal flex-shrink-0 mt-0.5" />
                                <div className="text-sm font-mono">
                                    <div className="text-signal font-bold uppercase tracking-wider text-xs mb-1">Account Created</div>
                                    <div className="text-snow-dim">
                                        Your account is now <span className="text-signal">Pending administrator approval</span>.
                                        You'll be able to sign in once an admin authorizes your access.
                                    </div>
                                </div>
                            </div>
                            <Link
                                to="/login"
                                className="w-full bg-signal text-jet hover:bg-signal-hover px-6 py-3 transition-colors font-bold uppercase tracking-[0.2em] flex items-center justify-center gap-2"
                                data-testid="invite-success-login-link"
                            >
                                <Lock className="w-4 h-4" /> Go to Sign-In
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
